import React, { useState, useEffect } from 'react';
import { X, Loader2, CheckCircle2, UserPlus } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function InviteAdminModal({ isOpen, onClose, onInvited }) {
  const { getAuthHeader } = useAuth();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('admin');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');

  // Close on Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'Escape' && isOpen) handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Reset state when re-opened
  useEffect(() => {
    if (isOpen) {
      setName('');
      setEmail('');
      setRole('admin');
      setError('');
      setIsSent(false);
      setSentEmail('');
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email.trim()) {
      setError('Name and email are required');
      return;
    }

    setIsLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/admin/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({ name: name.trim(), email: email.trim(), role })
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setError(data.error || 'Failed to send invitation');
        setIsLoading(false);
        return;
      }

      setSentEmail(email.trim());
      setIsSent(true);
      if (onInvited) onInvited(data.admin);
    } catch (err) {
      setError('Network error. Please try again.');
    }
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(56, 57, 78, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 16
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="rounded-lg border border-border bg-card text-card-foreground shadow-sm"
        style={{
          width: '100%',
          maxWidth: 460,
          background: '#fff',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          padding: '20px 24px',
          borderBottom: '1px solid var(--border, #e5e7eb)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 8,
              background: 'rgba(57,91,129,0.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <UserPlus className="h-5 w-5" style={{ color: '#395b81' }} />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ margin: 0, color: '#38394e' }}>
                {isSent ? 'Invitation sent' : 'Invite admin'}
              </h3>
              <p className="text-sm text-muted-foreground" style={{ margin: 0, fontSize: 13 }}>
                {isSent ? 'They\'ll receive an email shortly' : 'Send an email invitation to a new admin'}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isLoading}
            style={{
              background: 'transparent', border: 'none', cursor: isLoading ? 'not-allowed' : 'pointer',
              color: '#9ca3af', padding: 4, display: 'flex'
            }}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 24 }}>
          {isSent ? (
            <div className="space-y-4">
              <div className="rounded-lg bg-green-50 border border-green-200 p-4 flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-green-800">
                  <p className="font-medium mb-1">Invitation sent to {sentEmail}</p>
                  <p>The link will expire in 24 hours. They'll appear as "pending" until they accept.</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    setIsSent(false);
                    setName('');
                    setEmail('');
                    setRole('admin');
                  }}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-10 px-4"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    color: '#395b81',
                    border: '1px solid var(--border, #e5e7eb)'
                  }}
                >
                  Invite another
                </button>
                <button
                  onClick={handleClose}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-10 px-4"
                  style={{
                    flex: 1,
                    background: '#395b81',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-800">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label htmlFor="invite-name" className="text-sm font-medium leading-none">
                  Full name
                </label>
                <input
                  id="invite-name"
                  type="text"
                  placeholder="Jane Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={isLoading}
                  autoFocus
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="invite-email" className="text-sm font-medium leading-none">
                  Email
                </label>
                <input
                  id="invite-email"
                  type="email"
                  placeholder="jane@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                />
              </div>

              <div className="space-y-2">
                <label htmlFor="invite-role" className="text-sm font-medium leading-none">
                  Role
                </label>
                <select
                  id="invite-role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  disabled={isLoading}
                  className="flex h-11 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
                <p className="text-xs text-muted-foreground" style={{ fontSize: 12 }}>
                  {role === 'superadmin'
                    ? 'Superadmins can invite other admins.'
                    : 'Admins can view and respond to messages.'}
                </p>
              </div>

              <div style={{ display: 'flex', gap: 8, paddingTop: 8 }}>
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isLoading}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-11 px-4 disabled:opacity-50"
                  style={{
                    flex: 1,
                    background: 'transparent',
                    color: '#38394e',
                    border: '1px solid var(--border, #e5e7eb)'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-11 px-4 disabled:pointer-events-none disabled:opacity-50"
                  style={{
                    flex: 1,
                    background: '#f0856d',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    'Send invitation'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default InviteAdminModal;